import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';

import Login from '@/pages/login';
import ToolEmbedPage from '@/pages/tools';
import { PlanningLayoutRoute } from './guards/PlanningLayoutRoute';
import { BareProtectedRoute, ProtectedRoute } from './guards/ProtectedRoute';
import { FullWidthLayoutRoute } from './guards/FullWidthLayoutRoute';

// Все страницы грузятся лениво — стартовый бандл маленький, первый показ быстрый
// независимо от того, куда зашёл пользователь. Сразу после первого рендера
// prefetchRoutes() в простое подтягивает чанки остальных страниц в фон, чтобы
// последующие переходы были мгновенными, без полноэкранного лоадера.
const pageLoaders = {
  tasks: () => import('@/pages/tasks'),
  chats: () => import('@/pages/chats'),
  projectsList: () => import('@/pages/projects'),
  projectDetail: () => import('@/pages/projects/detail'),
  groups: () => import('@/pages/groups'),
  disk: () => import('@/pages/disk'),
  directories: () => import('@/pages/directories'),
  contactCard: () => import('@/pages/contact-card'),
  dealCard: () => import('@/pages/deal-card'),
  finance: () => import('@/pages/finance'),
  payments: () => import('@/pages/payments'),
  pulse: () => import('@/pages/pulse'),
  taskView: () => import('@/pages/task-view'),
  taskRedesign: () => import('@/pages/task-redesign'),
  planning: () => import('@/pages/planning'),
  planningMobile: () => import('@/pages/planning/ui/PlanningMobilePage'),
  notFound: () => import('@/pages/not-found'),
};

const Tasks = lazy(pageLoaders.tasks);
const Chats = lazy(pageLoaders.chats);
const ProjectsList = lazy(pageLoaders.projectsList);
const ProjectDetail = lazy(pageLoaders.projectDetail);
const Groups = lazy(pageLoaders.groups);
const Disk = lazy(pageLoaders.disk);
const Directories = lazy(pageLoaders.directories);
const ContactCard = lazy(pageLoaders.contactCard);
const DealCardPage = lazy(pageLoaders.dealCard);
const Finance = lazy(pageLoaders.finance);
const Payments = lazy(pageLoaders.payments);
const Pulse = lazy(pageLoaders.pulse);
const TaskView = lazy(pageLoaders.taskView);
const TaskRedesignPage = lazy(pageLoaders.taskRedesign);
const PlanningPage = lazy(pageLoaders.planning);
const PlanningMobilePage = lazy(pageLoaders.planningMobile);
const NotFound = lazy(pageLoaders.notFound);

let routesPrefetched = false;

/**
 * Прогрев чанков всех страниц в фоне — вызывается один раз в простое после
 * первого рендера (см. AppRouter). Идемпотентно. Браузер кеширует модуль по
 * спецификатору импорта, тот же чанк использует lazy(), поэтому к моменту
 * перехода страница уже готова и Suspense-fallback («Загрузка...») не мелькает.
 */
export function prefetchRoutes() {
  if (routesPrefetched) {
    return;
  }
  routesPrefetched = true;
  for (const load of Object.values(pageLoaders)) {
    void load().catch(() => undefined);
  }
}

function TaskOverlay() {
  return (
    <div className="fixed inset-0 z-[999] bg-[#0B1A2A]">
      <TaskView />
    </div>
  );
}

/**
 * Оверлей детальной страницы (задача или карточка проекта) поверх той страницы,
 * с которой её открыли. Слой `fixed inset-0` кладёт её модалкой на весь экран, а
 * фон под ним остаётся смонтированным (фильтры списка, скролл, состояние вкладок
 * сохраняются). Активируется из AppRouter, когда есть фоновая локация; закрытие
 * (крестик, «назад» в UI или в браузере) возвращает на фон без перезагрузки.
 *
 * z-index — «страничный» (z-40), НЕ модальный: глобальные модалки (выбор
 * действия, продление, результат, автозакрытие и т.п.) портятся в body со своими
 * z (50…9999) и должны открываться ПОВЕРХ. Фон под оверлеем AppRouter прячет
 * через visibility, поэтому низкого z достаточно и фоновые навбар/шапки не
 * просвечивают. См. AppRouter.
 */
function ModalLayer({
  layout: Layout = ProtectedRoute,
}: {
  layout?: typeof ProtectedRoute;
}) {
  return (
    <div className="fixed inset-0 z-40 overflow-y-auto">
      <Layout />
    </div>
  );
}

/** Маршруты в режиме оверлея (см. AppRouter). Пути те же, что у обычных
 *  маршрутов ниже, которые остаются fallback'ом для прямого захода/перезагрузки. */
export const modalRoutes: RouteObject[] = [
  {
    // Карточка задачи сверстана во всю ширину (свой фон, свой h-screen) — тот
    // же лэйаут без AppLayout-container, что у /finance, /edo, /contacts/:id.
    path: '/tasks-new/:id?',
    element: <ModalLayer layout={FullWidthLayoutRoute} />,
    children: [{ index: true, element: <TaskRedesignPage /> }],
  },
  {
    // Карточка проекта оформлена как карточка задачи — во всю ширину, со своей
    // тёмной шапкой, — поэтому и лэйаут у неё тот же, без AppLayout-container.
    path: '/projects/:entityTypeId/:id',
    element: <ModalLayer layout={FullWidthLayoutRoute} />,
    children: [{ index: true, element: <ProjectDetail /> }],
  },
];

export const routes: RouteObject[] = [
  { path: '/login', element: <Login /> },

  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        path: '/',
        element: <Tasks />,
        children: [
          { index: true, element: null },
          { path: 'tasks/:id', element: <TaskOverlay /> },
        ],
      },
    ],
  },

  {
    path: '/chats',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <Chats /> }],
  },
  {
    path: '/groups',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <Groups /> }],
  },
  {
    path: '/groups/:projectId',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <Groups /> }],
  },
  {
    path: '/disk',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <Disk /> }],
  },
  {
    path: '/directories',
    element: <ProtectedRoute />,
    children: [
      { index: true, element: <Navigate to="/directories/contacts" replace /> },
      { path: ':type', element: <Directories /> },
    ],
  },
  // Карточка контакта (прототипы «Карточка контакта» и «Карточка контакта
  // мобайл»). Своя тёмная шапка и лента вкладок, поэтому лэйаут без глобального
  // Header и без ограничения ширины — как у «Финансов» и «ЭДО».
  // `/contacts/demo` открывает карточку целиком на данных прототипа.
  {
    path: '/contacts/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <ContactCard /> }],
  },
  // Карточки сделки и лида (перенос из чекерборда, widgets/deal-card): общий
  // экран на две сущности, данные — из Справочников. Лэйаут тот же, что у
  // карточки контакта: своя тёмная шапка, футер этапов во всю ширину.
  {
    path: '/deals/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <DealCardPage entity="deals" /> }],
  },
  {
    path: '/leads/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <DealCardPage entity="leads" /> }],
  },
  // Раздел «Финансы» в сайдбаре (прототип «Модуль казначейства»): дашборд,
  // платёжный календарь, воронка оплат, прогноз, разрыв, кассы, банк.
  {
    path: '/finance',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <Finance /> }],
  },
  // Раздел «ЭДО» в сайдбаре: договоры, конструктор, шаблоны (прототип «Модуль оплат»).
  {
    path: '/edo',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <Payments /> }],
  },
  // Раздел «Операционный пульс» в сайдбаре (прототип «Пульс — Операции»):
  // вкладки Проекты и Отделы, боковые панели разбора.
  {
    path: '/pulse',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <Pulse /> }],
  },
  {
    path: '/projects',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <ProjectsList /> }],
  },
  {
    path: '/projects/:entityTypeId/:id',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <ProjectDetail /> }],
  },
  {
    path: '/tools/:toolId',
    element: <ProtectedRoute />,
    children: [{ index: true, element: <ToolEmbedPage /> }],
  },
  {
    path: '/tasks-new/:id?',
    element: <FullWidthLayoutRoute />,
    children: [{ index: true, element: <TaskRedesignPage /> }],
  },
  {
    path: '/planning',
    element: <PlanningLayoutRoute />,
    children: [{ index: true, element: <PlanningPage /> }],
  },
  {
    path: '/planning/mobile',
    element: <BareProtectedRoute />,
    children: [{ index: true, element: <PlanningMobilePage /> }],
  },
  {
    path: '/planning/:entityTypeId/:id',
    element: <PlanningLayoutRoute />,
    children: [{ index: true, element: <PlanningPage /> }],
  },

  { path: '*', element: <NotFound /> },
];
